import React, { useEffect, useState } from "react";
import {
  Form, Input, InputNumber, Select, Switch, Divider, Button, Space,
  Typography, Empty, App,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { FormDrawer, ImageUrlInput } from "../../../components/admin";
import { DIFFICULTY_LEVEL } from "../../../constants/apiEnums";
import { workoutService } from "../services/workoutService";
import { difficultyLabel } from "../data/enumLabels";
import { AttributeScoresEditor } from "./AttributeScoresEditor";

const { Text } = Typography;

// Exercises are managed through a *separate* replace-all endpoint in the
// real API (PUT /api/admin/workouts/{id}/exercises — see
// AdminWorkoutController.SetExercises), so they get their own "Save
// Exercises" action here rather than being bundled into the workout
// upsert payload. That also means a brand-new (unsaved) workout has
// nowhere to attach exercises to yet.
function ExercisesEditor({ workoutId, exerciseOptions }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workoutId) return;
    setLoading(true);
    workoutService.getExercises(workoutId).then((res) => {
      form.setFieldsValue({ exercises: res.data });
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const exercises = values.exercises.map((ex, i) => ({
        ...ex,
        order: i + 1,
        exerciseName: exerciseOptions.find((o) => o.id === ex.exerciseId)?.name ?? "",
      }));
      await workoutService.setExercises(workoutId, { exercises });
      message.success("Egzersizler kaydedildi.");
    } finally {
      setSaving(false);
    }
  };

  if (!workoutId) {
    return <Empty description="Egzersiz eklemek için önce antrenmanı kaydedin." />;
  }

  return (
    <Form form={form} layout="vertical" disabled={loading}>
      <Form.List name="exercises">
        {(fields, { add, remove }) => (
          <>
            {fields.length === 0 && !loading && (
              <Empty description="Henüz egzersiz yok." style={{ marginBottom: 12 }} />
            )}
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              {fields.map((field, index) => (
                <Space key={field.key} align="baseline" style={{ width: "100%" }} wrap>
                  <Text type="secondary" style={{ width: 20 }}>{index + 1}.</Text>
                  <Form.Item
                    name={[field.name, "exerciseId"]}
                    rules={[{ required: true, message: "Zorunlu alan" }]}
                    style={{ marginBottom: 0, minWidth: 180 }}
                  >
                    <Select
                      placeholder="Egzersiz"
                      options={exerciseOptions.map((o) => ({ value: o.id, label: o.name }))}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                  <Form.Item name={[field.name, "sets"]} style={{ marginBottom: 0 }} initialValue={3}>
                    <InputNumber min={1} max={50} addonBefore="Set" style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item name={[field.name, "reps"]} style={{ marginBottom: 0 }} initialValue={10}>
                    <InputNumber min={1} max={500} addonBefore="Tekrar" style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item name={[field.name, "restSeconds"]} style={{ marginBottom: 0 }} initialValue={45}>
                    <InputNumber min={0} max={1800} addonBefore="Dinlenme sn" style={{ width: 130 }} />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </Space>
              ))}
            </Space>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({ sets: 3, reps: 10, restSeconds: 45 })}
              style={{ marginTop: 12 }}
              block
            >
              Egzersiz Ekle
            </Button>
          </>
        )}
      </Form.List>
      <Button type="primary" onClick={handleSave} loading={saving} style={{ marginTop: 16 }}>
        Egzersizleri Kaydet
      </Button>
    </Form>
  );
}

export default function WorkoutFormDrawer({
  open, workout, categories, muscleGroups, exerciseOptions, submitting, onClose, onSubmit,
}) {
  const [form] = Form.useForm();
  const isEdit = Boolean(workout);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(
        workout ?? { durationMin: 30, difficulty: "Beginner", isFeatured: false, isPremium: false, isActive: true },
      );
    }
  }, [open, workout, form]);

  const handleSubmit = () => {
    form.validateFields().then((values) => onSubmit(values));
  };

  return (
    <FormDrawer
      open={open}
      title={isEdit ? `"${workout?.title}" Düzenle` : "Antrenman Oluştur"}
      submitText={isEdit ? "Değişiklikleri Kaydet" : "Antrenman Oluştur"}
      submitting={submitting}
      width={640}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Başlık"
          rules={[{ required: true, min: 2, max: 200, message: "2-200 karakter olmalıdır" }]}
        >
          <Input placeholder="örn. Tüm Vücut Yakımı" />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Slug"
          rules={[
            { required: true, min: 2, max: 200, message: "2-200 karakter olmalıdır" },
            { pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/, message: "Yalnızca küçük harf, rakam ve tire kullanılabilir" },
          ]}
        >
          <Input placeholder="örn. tum-vucut-yakimi" />
        </Form.Item>
        <Form.Item name="tagline" label="Slogan" rules={[{ max: 300 }]}>
          <Input placeholder="Kısa tek satırlık özet" />
        </Form.Item>
        <Form.Item name="description" label="Açıklama" rules={[{ max: 4000 }]}>
          <Input.TextArea rows={3} placeholder="Antrenmanın tam açıklaması" />
        </Form.Item>
        <Space size={16} style={{ width: "100%" }}>
          <Form.Item
            name="durationMin"
            label="Süre (dk)"
            rules={[{ required: true, type: "number", min: 1, max: 600 }]}
            style={{ flex: 1 }}
          >
            <InputNumber style={{ width: "100%" }} min={1} max={600} />
          </Form.Item>
          <Form.Item
            name="difficulty"
            label="Zorluk"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select options={DIFFICULTY_LEVEL.map((d) => ({ value: d, label: difficultyLabel(d) }))} />
          </Form.Item>
        </Space>
        <Space size={16} style={{ width: "100%" }}>
          <Form.Item
            name="categoryId"
            label="Kategori"
            rules={[{ required: true, message: "Kategori zorunludur" }]}
            style={{ flex: 1 }}
          >
            <Select
              placeholder="Kategori seçin"
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="muscleGroupId" label="Kas Grubu" style={{ flex: 1 }}>
            <Select
              placeholder="Kas grubu seçin"
              allowClear
              options={muscleGroups.map((m) => ({ value: m.id, label: m.name }))}
            />
          </Form.Item>
        </Space>
        <Form.Item
          name="imageUrl"
          label="Görsel URL"
          rules={[{ type: "url", message: "Geçerli bir URL girin" }]}
        >
          <ImageUrlInput previewAlt="Antrenman görseli önizlemesi" />
        </Form.Item>
        <Space size={32}>
          <Form.Item name="isFeatured" label="Öne Çıkan" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isPremium" label="Premium" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isActive" label="Aktif" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
      </Form>

      <Divider>Egzersizler</Divider>
      <ExercisesEditor workoutId={workout?.id} exerciseOptions={exerciseOptions} />

      <Divider>Özellik Puanları</Divider>
      <AttributeScoresEditor workoutId={workout?.id} />
    </FormDrawer>
  );
}
